import { z } from 'zod';
import { ScopeOwnershipSchema, OfferDepartmentSchema } from './permissions.js';

const UserAdminRoleSchema = z.object({
  id: z.number(),
  code: z.string(),
  name: z.string(),
});

export const UserAdminSchema = z.object({
  id: z.number(),
  email: z.string().email(),
  name: z.string(),
  roles: z.array(UserAdminRoleSchema),
  isActive: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type UserAdmin = z.infer<typeof UserAdminSchema>;

export const UserAdminListItemSchema = z.object({
  id: z.number(),
  email: z.string().email(),
  name: z.string(),
  roles: z.array(UserAdminRoleSchema),
  isActive: z.boolean(),
  createdAt: z.coerce.date(),
});
export type UserAdminListItem = z.infer<typeof UserAdminListItemSchema>;

// Length-only minimums accept weak-but-long passwords (e.g. 'aaaaaaaaaaaa').
// Require at least one digit or symbol without mandating a specific mix of
// upper/lower/digit/symbol classes -- NIST SP 800-63B favors length over
// strict composition rules. \p{P}/\p{S} (Unicode punctuation/symbol) is used
// instead of a negated `[^A-Za-z]` class -- the negated form would wrongly
// accept accented letters (Polish 'ą', 'ł') or whitespace as satisfying the
// rule, since they also fall outside plain ASCII A-Za-z.
//
// Exported as standalone constants (not just baked into adminPasswordSchema)
// so consumers that need translated error messages -- e.g. the admin web
// dialog's client-side zod schema -- can build their own `.min()`/`.regex()`
// chain against the exact same rule instead of hand-copying the numbers.
export const ADMIN_PASSWORD_MIN_LENGTH = 12;
export const ADMIN_PASSWORD_REGEX = /[0-9\p{P}\p{S}]/u;

// Shared by CreateUserSchema (new user) and AdminSetPasswordSchema (admin
// resets an existing user's password) so both paths enforce the same policy.
export const adminPasswordSchema = z
  .string()
  .min(ADMIN_PASSWORD_MIN_LENGTH, 'Password must be at least 12 characters')
  .regex(ADMIN_PASSWORD_REGEX, 'Password must contain at least one number or symbol');

export const CreateUserSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1),
  roleIds: z.array(z.number().int().positive()).nonempty(),
  password: adminPasswordSchema,
});
export type CreateUser = z.infer<typeof CreateUserSchema>;

// Solution B (admin stopgap): an admin sets/changes an existing user's password
// via a dedicated endpoint. Deliberately NOT merged into UpdateUserSchema --
// the update path audit-logs `{ changes: patch }` verbatim (see
// users-admin.service.ts updateUser), which would leak the plaintext password
// into the audit_logs table if password lived on that schema.
export const AdminSetPasswordSchema = z
  .object({
    password: adminPasswordSchema,
  })
  .strict();
export type AdminSetPassword = z.infer<typeof AdminSetPasswordSchema>;

export const UpdateUserSchema = z
  .object({
    name: z.string().min(1),
    roleIds: z.array(z.number().int().positive()).nonempty(),
    isActive: z.boolean(),
  })
  .partial()
  .strict();
export type UpdateUser = z.infer<typeof UpdateUserSchema>;

export const UserPermissionOverrideSchema = z.object({
  permissionCode: z.string().min(1),
  granted: z.boolean(),
  mediaType: z.string().nullable().optional(),
  ownership: ScopeOwnershipSchema.nullable().optional(),
  department: OfferDepartmentSchema.nullable().optional(),
});
export type UserPermissionOverride = z.infer<typeof UserPermissionOverrideSchema>;

export const UpdateUserPermissionOverridesSchema = z.object({
  overrides: z.array(UserPermissionOverrideSchema),
});
export type UpdateUserPermissionOverrides = z.infer<typeof UpdateUserPermissionOverridesSchema>;
